import { Globe, Music, Instagram, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { DottedSurface } from '@/components/ui/dotted-surface';
import auraMascot from '@/assets/aura-mascot.png';

const links = [
  {
    label: 'Website',
    url: 'https://facelessaura.club/',
    icon: Globe,
  },
  {
    label: 'Spotify Podcast',
    url: 'https://open.spotify.com/show/1VnoF1p9A8NpZlHGFv60B3?si=d9027150845f48c0',
    icon: Music,
  },
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/facelessaura.club/',
    icon: Instagram,
  },
  {
    label: 'TikTok',
    url: 'https://www.tiktok.com/@facelessaura.club',
    icon: ExternalLink,
  },
];

const Links = () => {
  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex items-center justify-center bg-background">
      <DottedSurface />

      {/* Content */}
      <div className="relative z-10 w-full max-w-sm mx-auto px-5 py-12 flex flex-col items-center gap-6 safe-area-inset">
        {/* Mascot avatar */}
        <motion.div
          className="relative w-24 h-24"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="absolute -inset-3 rounded-full bg-primary/15 blur-xl animate-pulse" />
          <img
            src={auraMascot}
            alt="Faceless Aura Club"
            className="relative w-full h-full rounded-full object-cover border-2 border-primary/20 shadow-lg shadow-primary/10"
          />
        </motion.div>

        {/* Title */}
        <motion.div
          className="text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-[260px] mx-auto">
            Generating Wealth with Online Businesses Powered by AI.
          </p>
        </motion.div>

        {/* Links */}
        <div className="w-full flex flex-col gap-3">
          {links.map((link, i) => (
            <motion.a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-full flex items-center gap-4 px-5 py-4 rounded-xl bg-card/80 backdrop-blur-sm border border-border/60 hover:border-primary/30 hover:shadow-md hover:shadow-primary/5 active:scale-[0.97] transition-all duration-200"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.35 + i * 0.08, duration: 0.5 }}
              whileTap={{ scale: 0.97 }}
            >
              <link.icon size={20} className="shrink-0 text-primary/70" />
              <span className="text-sm font-semibold tracking-wide text-foreground">
                {link.label}
              </span>
              <ExternalLink size={14} className="ml-auto shrink-0 text-muted-foreground/40" />
            </motion.a>
          ))}
        </div>

        {/* Footer */}
        <motion.p
          className="text-[10px] tracking-widest uppercase mt-3 text-muted-foreground/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          © 2025 Faceless Aura Club
        </motion.p>
      </div>

      <style>{`
        .safe-area-inset {
          padding-bottom: max(env(safe-area-inset-bottom, 0px), 3rem);
          padding-top: max(env(safe-area-inset-top, 0px), 3rem);
        }
      `}</style>
    </div>
  );
};

export default Links;
